import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

interface Props {
  id: string;
  tag: string | undefined;
  robGroup: string;
}

/**
 * Custom hook to calculate and update Onyx ROB percentage calculations.
 * Takes the value of MDO ROB and divides by 768 * 100 to get the percentage.
 * Formula: (VALUE / 768) * 100
 *
 * @param props - An object containing the following properties:
 *   - id: A string representing the unique identifier for the ROB input.
 *   - tag: tag that sits in input obj (OnyxROBTotal for ROB fields, OnyxPercTotal for percentage fields).
 *   - robGroup: identifier for the ROB group (e.g., "rob-1", "rob-2").
 */
export const useOnyxROBTotal = ({ id, tag, robGroup }: Props) => {
  const { setValue } = useFormContext();

  useEffect(() => {
    // Only run for onyx ROB and percentage fields
    if (
      !tag ||
      (!tag.startsWith("OnyxROBTotal") && !tag.startsWith("OnyxPercTotal"))
    )
      return;

    const currentElement = document.querySelector(
      `[name="${id}"]`,
    ) as HTMLInputElement;
    if (!currentElement) return;

    // Add appropriate classes to current element
    if (tag.startsWith("OnyxROBTotal")) {
      currentElement.classList.add("onyx-rob", robGroup);
    } else if (tag.startsWith("OnyxPercTotal")) {
      currentElement.classList.add("onyx-percentage", robGroup);
    }

    // Only perform conversion if this is a ROB input (not percentage input)
    if (!tag.startsWith("OnyxROBTotal")) return;

    const handleROBConversion = () => {
      const value = currentElement.value;
      const robNumber = parseFloat(value);

      // Find the corresponding percentage field using class
      const percentageElement = document.querySelector(
        `.onyx-percentage.${robGroup}`,
      ) as HTMLInputElement;

      if (!percentageElement || !percentageElement.name) return;

      if (
        !isNaN(robNumber) &&
        robNumber !== null &&
        robNumber !== undefined &&
        value !== ""
      ) {
        const percentage = +((robNumber / 768) * 100).toFixed(2);
        setValue(percentageElement.name, percentage);
      } else {
        setValue(percentageElement.name, "");
      }
    };

    currentElement.addEventListener("change", handleROBConversion);

    return () => {
      currentElement.removeEventListener("change", handleROBConversion);
    };
  }, [id, tag, robGroup, setValue]);
};
